import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/contexts/AuthContext';
import { getMessages, getConversation, sendMessage, getUserProfile } from '@/lib/api';
import { toast } from 'sonner';
import { Send, MessageSquare, ArrowLeft, User } from 'lucide-react';

const MessagesPage = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (userId) {
      loadConversation();
      loadOtherUser();
    }
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      const data = await getMessages();
      // Group messages by conversation partner
      const grouped = {};
      data.forEach(msg => {
        const partnerId = msg.sender_id === user.user_id ? msg.receiver_id : msg.sender_id;
        if (!grouped[partnerId]) {
          grouped[partnerId] = {
            partnerId,
            lastMessage: msg,
            partnerName: msg.sender_id === user.user_id ? 'Unknown' : msg.sender_name
          };
        } else if (new Date(msg.created_at) > new Date(grouped[partnerId].lastMessage.created_at)) {
          grouped[partnerId].lastMessage = msg;
        }
      });
      setConversations(Object.values(grouped));
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async () => {
    try {
      const data = await getConversation(userId);
      setMessages(data);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const loadOtherUser = async () => {
    try {
      const data = await getUserProfile(userId);
      setOtherUser(data);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !userId) return;

    setSending(true);
    try {
      const sent = await sendMessage({
        receiver_id: userId,
        message: newMessage.trim()
      });
      setMessages([...messages, sent]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-20">
      <div className="container-luxury py-8">
        <div className="bg-white rounded-sm shadow-sm border border-border/50 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 h-[calc(100vh-200px)]">
            {/* Conversations List */}
            <div className={`border-r border-border ${userId ? 'hidden md:block' : ''}`}>
              <div className="p-4 border-b">
                <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A]">Messages</h2>
              </div>
              <ScrollArea className="h-[calc(100%-60px)]">
                {loading ? (
                  <div className="p-4 space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="flex items-center gap-3 animate-pulse">
                        <div className="h-12 w-12 rounded-full bg-gray-200" />
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                          <div className="h-3 bg-gray-200 rounded w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : conversations.length > 0 ? (
                  <div>
                    {conversations.map((conv) => (
                      <Link
                        key={conv.partnerId}
                        to={`/messages/${conv.partnerId}`}
                        className={`flex items-center gap-3 p-4 hover:bg-[#F9F9F7] transition-colors ${
                          userId === conv.partnerId ? 'bg-[#F9F9F7] border-l-2 border-[#D4AF37]' : ''
                        }`}
                        data-testid={`conversation-${conv.partnerId}`}
                      >
                        <div className="h-12 w-12 rounded-full bg-[#0F172A] flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-medium">
                            {conv.partnerName?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-[#0F172A] truncate">{conv.partnerName || 'Unknown'}</p>
                            <span className="text-xs text-muted-foreground">{formatTime(conv.lastMessage.created_at)}</span>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {conv.lastMessage.sender_id === user.user_id ? 'You: ' : ''}
                            {conv.lastMessage.message}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">No messages yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Start a conversation by contacting a property owner
                    </p>
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Chat Area */}
            <div className="col-span-2 flex flex-col">
              {userId ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b flex items-center gap-4">
                    <Link to="/messages" className="md:hidden">
                      <Button variant="ghost" size="icon">
                        <ArrowLeft className="h-5 w-5" />
                      </Button>
                    </Link>
                    {otherUser ? (
                      <>
                        <div className="h-10 w-10 rounded-full bg-[#0F172A] flex items-center justify-center">
                          {otherUser.picture ? (
                            <img src={otherUser.picture} alt={otherUser.name} className="h-10 w-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-white font-medium">{otherUser.name?.[0]?.toUpperCase()}</span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#0F172A]">{otherUser.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{otherUser.user_type}</p>
                        </div>
                      </>
                    ) : (
                      <div className="animate-pulse flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gray-200" />
                        <div className="h-4 bg-gray-200 rounded w-24" />
                      </div>
                    )}
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.message_id}
                          className={`flex ${msg.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg px-4 py-2 ${
                              msg.sender_id === user.user_id
                                ? 'bg-[#0F172A] text-white'
                                : 'bg-[#F1F5F9] text-[#0F172A]'
                            }`}
                          >
                            <p className="break-words">{msg.message}</p>
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === user.user_id ? 'text-white/70' : 'text-muted-foreground'
                            }`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <form onSubmit={handleSend} className="p-4 border-t">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1"
                        data-testid="message-input"
                      />
                      <Button type="submit" className="btn-primary" disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="text-xl font-medium text-[#0F172A] mb-2">Select a Conversation</h3>
                    <p className="text-muted-foreground">Choose from your existing conversations or start a new one</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
